import { RootState } from "@/redux/store";
import { useAddRuleMutation, useGetDistributionMetricByRuleId, useGetDistributionRuleHierarchy, useGetRuleById, useUpdateRuleMutation } from "@/services";
import { useSelector } from "react-redux";
import { RuleBudgetType } from "../types";
import { BLOCK_TO_PROPERTY, COMMUNITY_TO_ZONE, SQUARE_FOOTAGE, WEIGHING_FACTOR, WEIGHING_FACTORS, ZONE_TO_BLOCK } from "@/lib/utils";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "@/components/Toast";
import { ALLOCATIONS, RECEIVER_BLOCK, RECEIVER_PROPERTY, RECEIVER_ZONE, RULE_NAME, SENDER_BLOCK, SENDER_ZONE, VALUE } from "./consts";
import { Button } from "@/components/ui";
import { FormInput } from "@/components";
import { CircleMinus, CirclePlus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

const useConfigureDialog = ({
  setOpen,
  level,
  budgetType,
  configuredData
}: {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  level: string,
  budgetType: RuleBudgetType,
  configuredData: any
}) => {
  const queryClient = useQueryClient();

  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  const levelSplit = level?.split("_");
  const levelValue = levelSplit[levelSplit.length - 1];
  const isView = configuredData?.action == "view";
  const isEdit = configuredData?.action == "edit";
  const isAdd = configuredData?.action == "add";

  const form = useForm<any>({
    defaultValues: {
      [RULE_NAME]: "",
      [ALLOCATIONS]: []
    }
  });

  const allocations = useWatch({control: form.control, name: ALLOCATIONS});

  useEffect(() => {
    queryClient.removeQueries({queryKey: ["distribution-rule-level", communityData?.communityId, currentFinanceYear, level, budgetType, configuredData?.key] })
  }, []);

  const {data: levelData, isLoading: isLoadingLevel, refetch: refetchRuleLevel} = useGetRuleById({
    communityId: communityData?.communityId,
    ruleId: configuredData?.distributionRuleId,
    queryParams: {
      enabled: false
    }
  });

  const {data: metricData, isLoading: isLoadingMetricInfo, refetch: refetchMetric } = useGetDistributionMetricByRuleId({
    communityId: communityData?.communityId,
    ruleId: configuredData?.distributionRuleId,
    queryParams: {
      enabled: false
    }
  });

  const isMetric = useMemo(() => configuredData?.key == SQUARE_FOOTAGE || configuredData?.key == WEIGHING_FACTOR || configuredData?.key == WEIGHING_FACTORS, [configuredData]);
  
  useEffect(() => {
    if (!isAdd) {
      if (isMetric) {
        refetchMetric()
      } else {
        refetchRuleLevel()
      }
    }
  }, [configuredData, isMetric])

  const {data: hierarchy, isLoading: isLoadingHierarchy} = useGetDistributionRuleHierarchy({
    communityId: communityData?.communityId,
    level: levelValue,
    queryParams: {
      enabled: !!levelValue
    }
  })
  
  useEffect(() => {
    if (hierarchy?.data) {
      let formattedData: any = []
    
      if (level == COMMUNITY_TO_ZONE) {
        const children = hierarchy?.data?.map((data: any) => ({
          id: data?.zoneId,
          label: data?.zoneName,
          percentage: '0',
          [VALUE]: isMetric ? 
            Number(metricData?.data?.nodes?.find((item: any) => item?.id == data?.zoneId)?.metricValue || "0").toFixed(2)
          : levelData?.data?.allocations?.find((item: any) => item?.[RECEIVER_ZONE] == data?.zoneId)?.[VALUE] || '0'
        }))

        const total = children?.reduce(
          (acc: number, val: any) => {
            const num = parseFloat(val?.[VALUE]);
            return acc + (isNaN(num) ? 0 : num);
          },
          0
        );

        formattedData = [{
            id: communityData?.communityId,
            label: communityData?.communityName || communityData?.name,
            isCollapsed: false,
            total,
            children
          }]
      } else if (level == ZONE_TO_BLOCK) {
        formattedData = hierarchy?.data?.map((data: any) => {
          const children = data?.blocks?.map((val: any) => ({
            id: val?.blockId,
            label: val?.blockName,
            percentage: '0',
            [VALUE]: isMetric ? 
              Number(metricData?.data?.nodes?.find((item: any) => item?.parentId == data?.zoneId && item?.id == val?.blockId)?.metricValue || "0").toFixed(2)
            : levelData?.data?.allocations?.find((item: any) => item?.[SENDER_ZONE] == data?.zoneId && item?.[RECEIVER_BLOCK] == val.blockId)?.[VALUE] || '0'
          }))
  
          const total = children?.reduce(
            (acc: number, val: any) => {
              const num = parseFloat(val?.[VALUE]);
              return acc + (isNaN(num) ? 0 : num);
            },
            0
          );

          return ({
            id: data?.zoneId,
            label: data?.zoneName,
            isCollapsed: false,
            children,
            total
          })
        });
      } else if (level == BLOCK_TO_PROPERTY) {
        formattedData = hierarchy?.data?.map((data: any) => {
            return data?.blocks?.map((val: any) => {
              const children = val?.properties?.map((prop: any) => ({
                id: prop?.propertyId,
                label: prop?.name?.split(" - ")?.[0] || "",
                percentage: '0',
                [VALUE]: isMetric ? 
                  Number(metricData?.data?.nodes?.find((item: any) => item?.parentId == val?.blockId && item?.id == prop?.propertyId)?.metricValue || "0")?.toFixed(2)
                : levelData?.data?.allocations?.find((item: any) => item?.[SENDER_BLOCK] == val?.blockId && item?.[RECEIVER_PROPERTY] == prop.propertyId)?.[VALUE] || '0'
              }))
      
              const total = children?.reduce(
                (acc: number, val: any) => {
                  const num = parseFloat(val?.[VALUE]);
                  return acc + (isNaN(num) ? 0 : num);
                },
                0
              );

              return ({
                id: val?.blockId,
                label: `${data?.zoneName} - ${val?.blockName}`,
                isCollapsed: false,
                children,
                total
              })
          })
        })

        formattedData = formattedData?.flat();
      }
      form.reset({
        [ALLOCATIONS]: formattedData,
        [RULE_NAME]: isAdd ? "" : levelData?.data?.[RULE_NAME]
      });
    }
  }, [hierarchy, levelData, metricData]);

  const onToggleCollapse = (groupIndex: number) => {
    const currentGroup = form.getValues(`${ALLOCATIONS}.${groupIndex}`);
    if (currentGroup) {
      form.setValue(
        `${ALLOCATIONS}.${groupIndex}.isCollapsed`, 
        !currentGroup.isCollapsed,
        { shouldValidate: true, shouldDirty: true } // Ensures React elements pick up the change
      );
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "label",
      header: ({ table }: any) => <span className="w-[50px] text-wrap">{table.options.data?.[0]?.headerLabel || "Details"}</span>,
      size: 120
    },
    {
      accessorKey: "weight",
      header: "",
      size: 140,
      cell: ({ row }: any) => {     
        const fieldName = row.original.formFieldName; 
        
        return (
          <div className="w-[140px]">
            <FormInput
              name={fieldName}
              control={form.control} // Pass control via table meta context
              label={configuredData?.label}
              placeholder="Please enter"
              variant="float"
              className="h-[34px] rounded-[3px]"
              disabled={isView}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "percentage",
      header: ({ table }: any) => {
        const tableData = table.options.data;
        if (!tableData?.length) return <div>Total: 0.00</div>;
  
        const parentIndex = tableData[0].parentFormIndex;
  
        // Watch only the children array values for THIS specific table instance
        const liveChildren = useWatch({
          control: form.control,
          name: `${ALLOCATIONS}.${parentIndex}.children`,
          defaultValue: [],
        });
  
        // Calculate total on the fly as the user types
        const liveTotal = liveChildren.reduce((acc: number, child: any) => {
          const val = parseFloat(child?.[VALUE] || 0);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
  
        return <div>Total: {liveTotal.toFixed(2)}</div>;
      },
      size: 100,
      cell: ({ row }: any) => {
        const parentIndex = row.original.parentFormIndex;
  
        // 1. Get this individual input's current live value
        const currentValue = useWatch({
          control: form.control,
          name: row.original.formFieldName,
          defaultValue: 0,
        });
  
        // 2. Get all peer elements inside this specific subsection card
        const liveChildren = useWatch({
          control: form.control,
          name: `${ALLOCATIONS}.${parentIndex}.children`,
          defaultValue: [],
        });
  
        // 3. Compute live sum
        const liveTotal = liveChildren.reduce((acc: number, child: any) => {
          const val = parseFloat(child?.[VALUE] || 0);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
  
        const percentage = liveTotal > 0 ? (Number(currentValue || 0) * 100) / liveTotal : 0;
  
        return <span>{percentage.toFixed(2)} %</span>;
      }
    },
    {
      id: "collapse",
      header: ({ table }: any) => {
        const onToggleCollapse = table.options.data?.[0]?.onToggleCollapse;
        const isCollapsed = table.options.data?.[0]?.isCollapsed;
        
        return (
          <Button type="button" variant="link"
            className="flex w-full items-center justify-end gap-2 text-primary no-underline"
            onClick={() => onToggleCollapse?.()}
          >
            {isCollapsed ? "Expand" : "Collapse"}
            {isCollapsed ? <CirclePlus /> : <CircleMinus />}
          </Button>
      )},
      size: 50,
      cell: () => null,
    },
  ], []);
  
  const { mutate: addRule, isPending: isAddingRulePending } =
    useAddRuleMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpen(false)
        queryClient.invalidateQueries({queryKey: ["distribution-rule-community-year", communityData?.communityId, currentFinanceYear]})
        toast.success(`${configuredData?.label} Configuration Added Successfully`);
        
      },
    });

  const { mutate: updateRule, isPending: isUpdateRulePending } =
    useUpdateRuleMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpen(false)
        queryClient.invalidateQueries({queryKey: ["distribution-rule-community-year", communityData?.communityId, currentFinanceYear]})
        toast.success(`${configuredData?.label} Configuration Updated Successfully`);
        
      },
    });

    const onSave = (data: any) => {
      const allocations = data[ALLOCATIONS];

      const alloc: any = []
      if (level == COMMUNITY_TO_ZONE) {
        allocations?.map((data: any) => {
          data?.children?.filter((val: any) => Number(val?.[VALUE]))?.map((val: any) => alloc.push({ 
            [RECEIVER_ZONE]: val.id,
            [VALUE]: val?.[VALUE]
          }))
        })
      } else if (level == ZONE_TO_BLOCK) {
        allocations?.map((data: any) => {
          data?.children?.filter((val: any) => Number(val?.[VALUE]))?.map((val: any) => alloc.push({ 
            [SENDER_ZONE]: data.id,
            [RECEIVER_BLOCK]: val.id,
            [VALUE]: val?.[VALUE]
          }))
        })
      } else if (level == BLOCK_TO_PROPERTY) {
        allocations?.map((data: any) => {
          data?.children?.filter((val: any) => Number(val?.[VALUE]))?.map((val: any) => alloc.push({ 
            [SENDER_BLOCK]: data.id,
            [RECEIVER_PROPERTY]: val.id,
            [VALUE]: val?.[VALUE]
          }))
        })
      }

      if (isEdit) {
        updateRule({
          communityId: communityData?.communityId,
          ruleId: configuredData?.distributionRuleId,
          data:  alloc
        })
      } else {
        addRule({
          data: {
            [RULE_NAME]: data[RULE_NAME],
            communityId: communityData?.communityId,
            year: currentFinanceYear,
            level: level,
            budgetType: budgetType,
            distributionMethod: configuredData?.key,
            // description: "string",
            [ALLOCATIONS]: alloc
          }
        })
      }
    }

  return {
    form,
    isLoadingLevel,
    levelData,
    isLoadingHierarchy,
    hierarchy: hierarchy?.data,
    allocations,
    isAddingRulePending,
    onSave,
    columns,
    isView,
    onToggleCollapse,
    isLoadingMetricInfo,
    isAdd,
    isUpdateRulePending
  }
}

export default useConfigureDialog;